import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Panel } from "../components/Panel";
import { Select } from "../components/Field";
import { Button } from "../components/Button";
import {
  agentProposalsApi,
  type AgentProposalRow,
  type ProposalStatus,
} from "../api/topic-pages";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "applied", label: "Applied (audit)" },
  { value: "rejected", label: "Rejected" },
  { value: "reverted", label: "Reverted" },
  { value: "all", label: "All" },
];

function AutonomyBadge({ autonomy }: { autonomy: "auto" | "proposed" }) {
  const cls =
    autonomy === "auto"
      ? "bg-blue-500/15 text-blue-300 border-blue-500/40"
      : "bg-amber-500/20 text-amber-300 border-amber-500/40";
  return (
    <span className={`text-xs px-2 py-0.5 border rounded ${cls}`}>
      {autonomy === "auto" ? "auto-applied" : "proposal"}
    </span>
  );
}

function StatusBadge({ status }: { status: ProposalStatus }) {
  const map: Record<ProposalStatus, string> = {
    pending: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    applied: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    rejected: "bg-red-500/15 text-red-300 border-red-500/40",
    reverted: "bg-ink-500/30 text-paper-trace border-ink-500",
  };
  return (
    <span className={`text-xs px-2 py-0.5 border rounded ${map[status]}`}>
      {status}
    </span>
  );
}

function diffSummary(before: unknown, after: unknown, field: string): string {
  // Render a one-line summary of the change for the audit feed.
  if (before === null || before === undefined) {
    return `→ create ${field}`;
  }
  if (Array.isArray(before) && Array.isArray(after)) {
    return `${field}: [${before.join(", ")}] → [${after.join(", ")}]`;
  }
  return `${field}: ${JSON.stringify(before)} → ${JSON.stringify(after)}`;
}

function ProposalRow({ row, onAct, busyId }: {
  row: AgentProposalRow;
  onAct: (id: number, action: "approve" | "reject" | "revert") => void;
  busyId: number | null;
}) {
  const field = (row.target_pk.field as string) ?? "(unknown)";
  const isCreate = field === "create";
  // Topic-page create proposals support a Preview button that opens the
  // synthesized landing in a new tab. Other surfaces (tag_alias,
  // content_gap, external_opportunity, rank_drop, country_gap,
  // outreach_target) are editorial decisions with no rendered preview.
  // Preview opens the synthesized topic landing on next.<title>.com. We
  // can't link there directly because the admin session cookie isn't
  // scoped to that domain — instead we hit a same-host endpoint that
  // mints a signed token under the admin cookie and 302-redirects with
  // the token attached.
  const isPreviewable = row.surface === "topic_page" && isCreate;
  const previewUrl = isPreviewable
    ? `/api/admin/agent-proposals/${row.id}/preview`
    : null;
  const before = row.before_state?.[field];
  const after = row.after_state[field];
  const slugStr = row.after_state.slug != null ? String(row.after_state.slug) : "?";
  const summary = isCreate
    ? `Create new topic page: ${slugStr}`
    : diffSummary(before, after, field);
  const h1Str = row.after_state.h1 != null ? String(row.after_state.h1) : null;
  const ledeStr = row.after_state.lede != null ? String(row.after_state.lede) : null;
  const metaStr = row.after_state.meta_description != null
    ? String(row.after_state.meta_description) : null;

  const acting = busyId === row.id;

  return (
    <li className="border-b border-ink-700 py-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <AutonomyBadge autonomy={row.autonomy} />
            <StatusBadge status={row.status} />
            <span className="label">
              {new Date(row.created_at).toLocaleString()}
            </span>
            <span className="label">· {row.surface}</span>
          </div>
          <div className="font-serif text-paper text-sm mb-1 break-words">
            {summary}
          </div>
          {row.rationale && (
            <p className="text-paper-dim text-xs italic">
              {row.rationale}
            </p>
          )}
          {isCreate && h1Str && (
            <div className="mt-2 p-2 bg-ink-700/40 border border-ink-500 rounded">
              <p className="text-paper text-sm">
                <span className="label">H1: </span>
                {h1Str}
              </p>
              {ledeStr && (
                <p className="text-paper-dim text-xs mt-1">
                  <span className="label">Lede: </span>
                  {ledeStr}
                </p>
              )}
              {metaStr && (
                <p className="text-paper-dim text-xs mt-1">
                  <span className="label">Meta: </span>
                  {metaStr}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 items-end">
          {row.status === "pending" && (
            <>
              {previewUrl && (
                <Button
                  onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
                >
                  Preview ↗
                </Button>
              )}
              <Button
                disabled={acting}
                onClick={() => onAct(row.id, "approve")}
              >
                Approve
              </Button>
              <Button
                variant="danger"
                disabled={acting}
                onClick={() => onAct(row.id, "reject")}
              >
                Reject
              </Button>
            </>
          )}
          {row.status === "applied" && row.autonomy === "auto" && !isCreate && (
            <Button
              variant="danger"
              disabled={acting}
              onClick={() => onAct(row.id, "revert")}
            >
              Revert
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

export function AgentProposalsPage() {
  const [status, setStatus] = useState<string>("pending");
  const [busyId, setBusyId] = useState<number | null>(null);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["agent-proposals", status],
    queryFn: () => agentProposalsApi.list(status as any),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["agent-proposals"] });

  // The most recent approve's spawn outcome — surfaced below the queue
  // so the editor sees "auto-spawned topic_page proposal #X" or "hub not
  // auto-built: <reason>" without needing to dig through logs.
  const [lastSpawnNote, setLastSpawnNote] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: agentProposalsApi.approve,
    onSettled: refresh,
  });
  const reject = useMutation({
    mutationFn: agentProposalsApi.reject,
    onSettled: refresh,
  });
  const revert = useMutation({
    mutationFn: agentProposalsApi.revert,
    onSettled: refresh,
  });

  const onAct = async (id: number, action: "approve" | "reject" | "revert") => {
    setBusyId(id);
    setLastSpawnNote(null);
    try {
      if (action === "approve") {
        const res = await approve.mutateAsync(id);
        setLastSpawnNote(res.spawn_note ?? null);
      } else if (action === "reject") {
        await reject.mutateAsync(id);
      } else {
        await revert.mutateAsync(id);
      }
    } finally {
      setBusyId(null);
    }
  };

  const rows = data?.proposals ?? [];
  const counts = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="font-serif text-3xl text-paper mb-2">Agent proposals</h1>
      <p className="label mb-5">
        Marketing-agent decisions awaiting review or already applied.{" "}
        <span className="text-amber-glow">Proposals</span> need explicit approval.{" "}
        <span className="text-blue-300">Auto-applied</span> changes are an
        audit feed; revert anything that turned out wrong.
      </p>

      <Panel>
        <div className="flex gap-3 items-end mb-4">
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={STATUS_OPTIONS}
          />
          <div className="text-sm text-paper-dim">
            {Object.entries(counts).map(([s, n]) => (
              <span key={s} className="mr-3">
                <span className="label">{s}:</span> <span className="text-paper">{n}</span>
              </span>
            ))}
          </div>
        </div>

        {lastSpawnNote && (
          <div className="mb-4 p-3 border border-amber-glow/40 bg-amber-glow/5 rounded">
            <p className="text-sm text-paper">{lastSpawnNote}</p>
            <button
              onClick={() => setLastSpawnNote(null)}
              className="label text-amber-glow underline mt-1"
            >
              dismiss
            </button>
          </div>
        )}
        {error && <p className="text-red-400">Failed to load: {String(error)}</p>}
        {isLoading && <p className="label">Loading…</p>}
        {!isLoading && rows.length === 0 && (
          <p className="label py-6">
            No proposals matching this filter. Try{" "}
            <button
              onClick={() => setStatus("all")}
              className="text-amber-glow underline"
            >
              show all
            </button>{" "}
            to see the audit history.
          </p>
        )}
        <ul>
          {rows.map((r) => (
            <ProposalRow
              key={r.id} row={r}
              onAct={onAct} busyId={busyId}
            />
          ))}
        </ul>
      </Panel>
    </div>
  );
}
